import * as React from "react";
import CustomTooltip from "src/components/custom-tooltip";
import DashboardTooltipTeams from "src/components/dashboard-tooltip-teams";
import DashboardTooltip from "src/components/dashboard-tooltip";
import SingleFundamentalsMetric from "src/components/single-fundamentals-metric";
import { resolveObjPathString } from "src/utils/resolve-path";
import { Grid, Paper, Stack } from "@mui/material";
import styled from "styled-components";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import TemplateBarChart from "src/components/template/template-bar-chart";
import TemplatePieChart from "src/components/template/template-pie-chart";
import BubbleChartCard from "src/components/bubble-chart-card";
import type {
    BaseItemChart,
    FetchAction,
    TemplateItemChart,
    TemplateItemInterface,
    TemplateSpecialComponent,
} from "src/components/template/types";
import TemplateComposedChart from "src/components/template/template-composed-chart";
import TemplateList from "src/components/template/template-list";
import TemplateLineChart from "src/components/template/template-line-chart";
import StageDurations from "src/components/template/special/stage-durations";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TemplateLinkList from "src/components/template/template-link-list";
import TemplateSwitch from "src/components/template/template-switch";
import MrTimingStats from "src/components/mr-timing-stats";
import TemplateAreaChart from "src/components/template/template-area-chart";
import TemplateDropdown from "src/components/template/template-dropdown";
import TicketsBreakdown from "src/components/tickets-breakdown";
import EstimatedTotalsStack from "src/components/estimated-totals-stack";
import EstimatedActualTime from "src/components/estimated-actual-time";
import TemplateHeatmap from "src/components/template/template-heatmap";
import MeetingsCancelledRescheduled from "src/components/template/special/meetings-cancelled-rescheduled";
import Upskilling from "src/components/template/special/upskilling";
import TemplateSpiderChart from "src/components/template/template-spider-chart";
import type { TeamApi } from "src/scenes/teams/team/types";
import { resolveName } from "src/utils/templating";
import KnowledgebaseIconLink from "src/components/knowledgebase-icon-link";
import { getIsTemplateItemNotIntegrated } from "src/utils/is-not-integrated";
import TemplateNoIntegration from "src/components/template/template-no-integration";
import { useActiveIntegrationsTeams } from "src/scenes/teams/dashboard/active-integrations-provider-teams";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import TemplateItemEditOverlay from "src/components/template/template-item-edit-overlay";
import { InView } from "react-intersection-observer";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";
import NoOptInPlaceholder from "src/components/no-opt-in-placeholder";

const MetricTitle = styled.h2`
    font-size: 14px;
    font-weight: 400;
    margin-bottom: 0;
`;

const CardTitle = styled.h2`
    font-size: 14px;
    font-weight: 400;
`;

const Card = styled(Paper)`
    ${({ big, minHeight }) => {
        if (minHeight)
            return `
        min-height: ${minHeight};
        height: 100%;
        `;
        if (big) return `height: 17rem`;
        return `
        min-height: 120px;
        height: 100%;
        `;
    }};
    border-radius: ${({ top, bottom, right, left }) => {
        const br: string[] = ["0", "0", "0", "0"];
        if (top) {
            if (left) {
                br[0] = "14px";
            }
            if (right) {
                br[1] = "14px";
            }
        }
        if (bottom) {
            if (right) {
                br[2] = "14px";
            }
            if (left) {
                br[3] = "14px";
            }
        }
        return br.join(" ");
    }};
    ${({ theme }) => theme.breakpoints.down("md")} {
        border-radius: 14px 14px 0 0;
    }
`;

const TemplateItem = ({
    item,
    teamApi,
    viewType,
    fetchAction,
    templateIndex,
    rowIndex,
    itemIndex,
    itemCount,
    top,
    bottom,
    context,
    isSample,
}: {
    item: TemplateItemInterface;
    fetchAction: FetchAction;
    viewType: "user" | "team" | "repo" | "exec" | "exec-ts";
    teamApi?: TeamApi;
    templateIndex: number;
    rowIndex: number;
    itemIndex: number;
    itemCount: number;
    top: boolean;
    bottom: boolean;
    context: { [key: string]: string };
    isSample?: boolean;
}) => {
    const { onEditMode: onEditModeTemp } = useTemplateViewEdit();
    const onEditMode = onEditModeTemp && !isSample;
    const [title, setTitle] = React.useState(item.title.value);
    const [toggleValue, setToggleValue] = React.useState<string>(
        "toggle" in item ? item.toggle[0].value : ""
    );
    const [filterValue, setFilterValue] = React.useState<string | null>(
        "filter" in item && item.filter?.options && item.filter?.options.length
            ? item.filter.options[0].value
            : null
    );
    const [titleDropDownOpen, setTitleDropDownOpen] = React.useState(false);
    const [fullContext, setFullContext] = React.useState<{ title?: string } & FetchAction>();

    const { isIntegrated, isCentrallyManaged } = (
        viewType === "team" ? useActiveIntegrationsTeams : useActiveIntegrations
    )();

    React.useEffect(() => {
        setFullContext({ ...context, ...fetchAction, title });
    }, [
        context && JSON.stringify(context),
        title,
        fetchAction && fetchAction.data && JSON.stringify(fetchAction.data),
    ]);

    React.useEffect(() => {
        if ("filter" in item && item.filter?.options && item.filter?.options.length) {
            setToggleValue(item.filter.options[0].value);
        }
    }, ["filter" in item && item.filter?.options && item.filter?.options.length]);

    const notIntegrated = React.useMemo(
        () =>
            getIsTemplateItemNotIntegrated(
                [item.tooltip.dependencies],
                viewType === "repo"
                    ? {
                          github: isIntegrated.github || isCentrallyManaged?.github,
                          gitlab: isIntegrated.gitlab || isCentrallyManaged?.gitlab,
                          gitlabSelfManaged:
                              isIntegrated.gitlabSelfManaged ||
                              isCentrallyManaged?.gitlabSelfManaged,
                          slack: isIntegrated.slack || isCentrallyManaged?.slack,
                          google: isIntegrated.google || isCentrallyManaged?.google,
                          jira: isIntegrated.jira || isCentrallyManaged?.jira,
                          notion: isIntegrated.notion || isCentrallyManaged?.notion,
                          microsoft: isIntegrated.microsoft || isCentrallyManaged?.microsoft,
                      }
                    : isIntegrated
            ),
        [item.tooltip.dependencies, viewType, isIntegrated]
    );

    const getTooltipTitle = () => {
        if (titleDropDownOpen) return "";
        return teamApi ? (
            <DashboardTooltipTeams
                integrationsNeeded={item.tooltip.dependencies}
                text={resolveName(item.tooltip.text, fullContext)}
                teamApi={teamApi}
            />
        ) : (
            <DashboardTooltip
                integrationsNeeded={item.tooltip.dependencies}
                text={resolveName(item.tooltip.text, fullContext)}
            />
        );
    };

    if ("chart" in item || "list" in item || "specialComponent" in item || "toggle" in item) {
        const getChart = (
            selectedItem:
                | TemplateItemInterface
                | Omit<TemplateItemChart | TemplateSpecialComponent, keyof BaseItemChart>
        ) => {
            if (!selectedItem) {
                return null;
            }
            if (item.optInFeature) {
                const optedIn = resolveObjPathString(
                    fetchAction.data,
                    item.optInFeature.optedInProperty
                );
                if (optedIn === false) {
                    return (
                        <NoOptInPlaceholder
                            data={fetchAction.data}
                            optInFeature={item.optInFeature}
                        />
                    );
                }
            }
            if ("specialComponent" in selectedItem) {
                if (selectedItem.specialComponent === "stage-durations") {
                    return <StageDurations fetchAction={fetchAction} />;
                }
                if (selectedItem.specialComponent === "mr-timings") {
                    return <MrTimingStats data={fetchAction.data && fetchAction.data.timings} />;
                }
                if (selectedItem.specialComponent === "tickets-breakdown-full") {
                    return (
                        <TicketsBreakdown
                            data={
                                fetchAction.data &&
                                fetchAction.data.ticketsBreakdown &&
                                (fetchAction.data.ticketsBreakdown as { simple: unknown }).simple
                            }
                            dataExpanded={
                                fetchAction.data &&
                                fetchAction.data.ticketsBreakdown &&
                                (fetchAction.data.ticketsBreakdown as { expanded: unknown })
                                    .expanded
                            }
                            isBreakdownExpanded
                            context={context}
                            issuesOrPoints={undefined}
                        />
                    );
                }
                if (selectedItem.specialComponent === "tickets-breakdown-simple") {
                    return (
                        <TicketsBreakdown
                            data={
                                fetchAction.data &&
                                fetchAction.data.ticketsBreakdown &&
                                (fetchAction.data.ticketsBreakdown as { simple: unknown }).simple
                            }
                            dataExpanded={
                                fetchAction.data &&
                                fetchAction.data.ticketsBreakdown &&
                                (fetchAction.data.ticketsBreakdown as { expanded: unknown })
                                    .expanded
                            }
                            isBreakdownExpanded={false}
                            context={context}
                            issuesOrPoints={undefined}
                        />
                    );
                }
                if (selectedItem.specialComponent === "tickets-time-estimated-spent") {
                    return (
                        <>
                            <EstimatedTotalsStack
                                data={fetchAction.data && fetchAction.data.timeSpentEstimated}
                                filterEstimatesBy={filterValue}
                            />
                            <EstimatedActualTime
                                data={fetchAction.data && fetchAction.data.timeSpentEstimated}
                                filterEstimatesBy={filterValue}
                            />
                        </>
                    );
                }
                if (selectedItem.specialComponent === "meetings-cancelled-rescheduled") {
                    return (
                        <MeetingsCancelledRescheduled fetchAction={fetchAction} teamApi={teamApi} />
                    );
                }
                if (selectedItem.specialComponent === "upskilling") {
                    return <Upskilling fetchAction={fetchAction} />;
                }
            }
            if (notIntegrated && notIntegrated.length) {
                return <TemplateNoIntegration integrationCategories={notIntegrated} />;
            }
            if ("list" in selectedItem) {
                if (selectedItem.list.type === "link") {
                    return (
                        <TemplateLinkList
                            data={fetchAction.data && fetchAction.data[selectedItem.list.property]}
                            parameters={selectedItem.list}
                        />
                    );
                }
                return (
                    <TemplateList
                        data={
                            fetchAction.data &&
                            selectedItem.list &&
                            selectedItem.list.property &&
                            fetchAction.data[selectedItem.list.property]
                        }
                        parameters={selectedItem.list}
                    />
                );
            }
            if ("chart" in selectedItem) {
                if (selectedItem.chart.type === "bar")
                    return (
                        <TemplateBarChart
                            data={fetchAction.data && fetchAction.data[selectedItem.chart.property]}
                            parameters={selectedItem.chart}
                            context={context}
                        />
                    );

                if (selectedItem.chart.type === "area")
                    return (
                        <TemplateAreaChart
                            data={fetchAction.data && fetchAction.data[selectedItem.chart.property]}
                            parameters={selectedItem.chart}
                            context={context}
                        />
                    );

                if (selectedItem.chart.type === "pie") {
                    return (
                        <TemplatePieChart
                            data={
                                fetchAction.data &&
                                resolveObjPathString(
                                    fetchAction.data,
                                    resolveName(selectedItem.chart.property, fullContext)
                                )
                            }
                            parameters={selectedItem.chart}
                            context={context}
                        />
                    );
                }

                if (selectedItem.chart.type === "bubble")
                    return <BubbleChartCard fetchAction={fetchAction} parameters={selectedItem} />;

                if (selectedItem.chart.type === "composed")
                    return (
                        <TemplateComposedChart
                            data={fetchAction.data && fetchAction.data[selectedItem.chart.property]}
                            parameters={selectedItem.chart}
                        />
                    );

                if (selectedItem.chart.type === "heatmap")
                    return (
                        <TemplateHeatmap
                            data={fetchAction.data && fetchAction.data[selectedItem.chart.property]}
                            parameters={selectedItem.chart}
                        />
                    );

                if (selectedItem.chart.type === "spider")
                    return (
                        <TemplateSpiderChart
                            data={fetchAction.data && fetchAction.data[selectedItem.chart.property]}
                            parameters={selectedItem.chart}
                        />
                    );

                if (selectedItem.chart.type === "line")
                    return (
                        <TemplateLineChart
                            data={
                                fetchAction.data &&
                                resolveObjPathString(
                                    fetchAction.data,
                                    resolveName(selectedItem.chart.property, fullContext)
                                )
                            }
                            parameters={selectedItem.chart}
                            context={context}
                            filter={
                                selectedItem.chart.filterBy
                                    ? {
                                          property: selectedItem.chart.filterBy,
                                          value: filterValue,
                                      }
                                    : null
                            }
                            title={resolveName(selectedItem.title.value, fullContext)}
                        />
                    );
            }
            return null;
        };

        return (
            <InView>
                {({ inView, ref }) => (
                    <Grid
                        item
                        xs={item.sizing.xs}
                        md={item.sizing.md}
                        style={{ position: "relative" }}
                        ref={ref}
                    >
                        {onEditMode && (
                            <TemplateItemEditOverlay
                                isChart
                                item={item}
                                rowIndex={rowIndex}
                                itemIndex={itemIndex}
                                templateIndex={templateIndex}
                            />
                        )}
                        {item.noCard ? (
                            inView && getChart(item)
                        ) : (
                            <Card
                                top={top}
                                bottom={bottom}
                                left={itemIndex === 0}
                                right={itemIndex === itemCount}
                                big={!item.sizing.smallHeight}
                                minHeight={item.sizing.minHeight}
                            >
                                <Stack
                                    direction="column"
                                    ml={3}
                                    mr={0.5}
                                    pt={0.5}
                                    style={{ height: "100%" }}
                                >
                                    {item.title.options ? (
                                        <CustomTooltip title={getTooltipTitle()}>
                                            <FormControl
                                                fullWidth
                                                style={{ marginTop: "10px", marginBottom: "-7px" }}
                                            >
                                                <Select
                                                    disableUnderline
                                                    value={title}
                                                    onChange={(event) => {
                                                        setTitle(event.target.value);
                                                    }}
                                                    onOpen={() => setTitleDropDownOpen(true)}
                                                    onClose={() => setTitleDropDownOpen(false)}
                                                    variant="standard"
                                                >
                                                    {item.title.options.map((option) => (
                                                        <MenuItem value={option.value}>
                                                            {option.text}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </CustomTooltip>
                                    ) : (
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            style={{ width: "100%" }}
                                        >
                                            {!isSample && (
                                                <Stack direction="row" alignItems="center">
                                                    <CustomTooltip title={getTooltipTitle()}>
                                                        <CardTitle>
                                                            {resolveName(
                                                                item.title.value,
                                                                fullContext
                                                            )}
                                                        </CardTitle>
                                                    </CustomTooltip>
                                                    <KnowledgebaseIconLink
                                                        name={item.knowledgeBaseName}
                                                        markdownId={item.knowledgeMarkdownId}
                                                    />
                                                </Stack>
                                            )}
                                            {"toggle" in item && (
                                                <TemplateSwitch
                                                    options={item.toggle}
                                                    value={toggleValue}
                                                    setValue={setToggleValue}
                                                />
                                            )}
                                            {"filter" in item && item.filter && (
                                                <TemplateDropdown
                                                    dynamicOptions={item.filter.dynamicOptions}
                                                    options={item.filter.options}
                                                    value={filterValue}
                                                    setValue={setFilterValue}
                                                    data={fetchAction.data}
                                                />
                                            )}
                                        </Stack>
                                    )}

                                    <ShowWithSkeleton
                                        fetchAction={fetchAction}
                                        height="100%"
                                        width="95%"
                                        variant={undefined}
                                        streamingProperty={
                                            fetchAction.data &&
                                            fetchAction.data[item.skeleton.property]
                                        }
                                    >
                                        {inView &&
                                            ("toggle" in item
                                                ? toggleValue &&
                                                  getChart({
                                                      ...item.items[toggleValue],
                                                      title: item.title,
                                                  })
                                                : getChart(item))}
                                    </ShowWithSkeleton>
                                </Stack>
                            </Card>
                        )}
                    </Grid>
                )}
            </InView>
        );
    }
    if ("value" in item) {
        const resolveProperty = (property: string) => {
            return (
                fetchAction.data &&
                resolveObjPathString(fetchAction.data, resolveName(property, fullContext))
            );
        };
        const getValue = () => {
            const { property } = item.value;
            return resolveProperty(property);
        };

        const getBaseline = () => {
            if (!item.baseline) return null;
            const { property } = item.baseline;
            return resolveProperty(property);
        };

        return (
            <Stack
                direction="column"
                mt={1}
                ml={3}
                key={item.title.value}
                style={{ position: "relative" }}
            >
                {onEditMode && (
                    <TemplateItemEditOverlay
                        isChart={false}
                        item={item}
                        rowIndex={rowIndex}
                        itemIndex={itemIndex}
                        templateIndex={templateIndex}
                    />
                )}
                <CustomTooltip title={getTooltipTitle()}>
                    {item.title.options ? (
                        <FormControl fullWidth style={{ marginTop: "10px", marginBottom: "-7px" }}>
                            <Select
                                disableUnderline
                                value={title}
                                onChange={(event) => {
                                    setTitle(event.target.value);
                                }}
                                onOpen={() => setTitleDropDownOpen(true)}
                                onClose={() => setTitleDropDownOpen(false)}
                                variant="standard"
                            >
                                {item.title.options.map((option) => (
                                    <MenuItem value={option.value}>{option.text}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <MetricTitle>{resolveName(item.title.value, fullContext)}</MetricTitle>
                    )}
                </CustomTooltip>
                {notIntegrated && notIntegrated.length ? (
                    <TemplateNoIntegration integrationCategories={notIntegrated} />
                ) : (
                    <NoOptInPlaceholder data={fetchAction.data} optInFeature={item.optInFeature}>
                        <SingleFundamentalsMetric
                            fetchAction={fetchAction}
                            variant={item.variant}
                            value={getValue()}
                            baseline={getBaseline()}
                            ticketsTransitioned={false}
                            noBaseline={!item.baseline}
                            {...(item.otherProps &&
                                Object.keys(item.otherProps).reduce(
                                    (acc, prop) => ({
                                        ...acc,
                                        [prop]:
                                            item.otherProps &&
                                            item.otherProps[prop] &&
                                            (item.otherProps[prop].propertyPath
                                                ? resolveProperty(
                                                      item.otherProps[prop].propertyPath
                                                  )
                                                : resolveName(
                                                      item.otherProps[prop].value,
                                                      fullContext
                                                  )),
                                    }),
                                    {}
                                ))}
                        />
                    </NoOptInPlaceholder>
                )}
            </Stack>
        );
    }
    return null;
};

export default TemplateItem;
